import { useContext, useSyncExternalStore } from "react";
import { ReactReduxContext } from "react-redux";
import { useInRouterContext, useLocation } from "react-router";
import type { Store } from "@reduxjs/toolkit";
import { DEFAULT_PRODUCT } from "../../shared/products/keys";
import { activeProductFor, skipsHireShellReads } from "../components/products/activeProduct";
import type { IRootReducerState } from "../store/IRootReducer";

const noSubscription = () => () => undefined;

/**
 * True in skyPerformance IT's shell for someone holding no hire role: the shell's hire reads
 * (access scope, location scope, engagement flag, SharePoint status) are skipped there, since the
 * server refuses them (spec 2026-10-26 §4.7). False for everyone else, exactly as before.
 *
 * Safe without a store or a router (as several shell pieces are rendered in their tests): with no
 * store, it reads as holding a hire role, so nothing is skipped. The product is the page's own:
 * from the router's location inside a router (under a MemoryRouter jsdom's own address is "/",
 * review 03 P2-3), else from the browser's address.
 */
export function useSkipsHireShellReads(): boolean {
  const store = useContext(ReactReduxContext)?.store as Store<IRootReducerState> | undefined;
  const holdsHire = useSyncExternalStore(store ? store.subscribe : noSubscription, () =>
    store
      ? Boolean(store.getState().authReducer?.capabilities?.productRoles?.[DEFAULT_PRODUCT])
      : true,
  );
  // Whether a router wraps a mounted component never changes, so the hook order is stable.
  const inRouter = useInRouterContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const pathname = inRouter ? useLocation().pathname : window.location.pathname;
  return skipsHireShellReads(activeProductFor(pathname), holdsHire);
}
